import { useState, useEffect } from 'react';
import { useNotificationProvider } from 'hooks';
import updateTabApi from 'api/menus/updateTabApi';
import convertFromTabToGroupTab from '../libs/convertFromTabToGroupTab';
import countCheckedRow from '../libs/countCheckedRow';
import createSubtab from '../libs/createSubtab';
import handleAllChecked from '../libs/handleAllChecked';
import handleOpenTabs from '../libs/handleOpenTabs';
import handleRow from '../libs/handleRow';
import makeCheckedTabs from '../libs/makeCheckedTabs';
import { Tab, Type } from '../const';
import assignOrder from '../libs/assignOrder';

/**
 * @function useTabs
 * @param { Object } props
 * @param { boolean } props.open
 * @param { boolean } props.trgRst
 * @param { object } props.savingState
 * @param { object } props.savingHandler
 * @param { string } props.groupId
 * @param { array } props.selectedTabs
 * @param { array } props.tabs
 * @param { function } props.onMenu
 * @returns { object }
 */
function useTabs(props) {
	const { onData, onUpdate } = props.savingHandler;

	const initIcons = { add: true, delete: true };

	const { notifySuccess, notifyError } = useNotificationProvider();

	const [groupTabs, setGroupTabs] = useState(props.selectedTabs);
	const [filteredTabs, setFilteredTabs] = useState([]);
	const [chosenOptions, setChosenOptions] = useState([]);
	const [count, setCount] = useState(0);
	const [isDisabled, setIsDisabled] = useState(initIcons);
	const [isCheckAll, setIsCheckAll] = useState(false);
	const [loading, setLoading] = useState(false);
	const [modal, setModal] = useState(false);
	const [tabId, setTabId] = useState('');
	const [trgRstSubtab, setTrgRstSubtab] = useState(false);
	const [isOpenSubtab, setIsOpenSubtab] = useState(false);

	const onCloseModal = () => {
		props.savingHandler.onReset();
		props.savingHandler.onResetForUpdated();
		onModal(false);
		const results = groupTabs.map((tab) => {
			if (tab[Tab.Id] === tabId) {
				return { ...tab, open: false };
			}
			return tab;
		});
		setGroupTabs([...results]);
		onTrgRstSubTab();
	};

	const onTrgRstSubTab = () => setTrgRstSubtab(!trgRstSubtab);

	const onSaveSubtab = () => {
		const results = groupTabs.map((tab) => {
			if (tab[Tab.Id] === tabId) {
				return { ...tab, open: false };
			}
			return tab;
		});
		setGroupTabs([...results]);
		props.savingHandler.onSave();
		props.savingHandler.onResetForUpdated();
		onModal(false);
	};

	const onModal = (status) => setModal(status);

	const onToggle = (tabId) => {
		setTabId(tabId);
		const results = handleOpenTabs(groupTabs, tabId, props.savingState.isUpdated.subtab, onModal);
		setGroupTabs([...results]);
	};

	const onOptions = (values) => {
		setIsDisabled((prev) => ({ ...prev, add: values.length === 0 }));
		setChosenOptions([...values]);
	};

	const onAdd = () => {
		const results = convertFromTabToGroupTab(chosenOptions, props.groupId);
		const _results = assignOrder(Type.Tab, [...groupTabs, ...results]);
		setGroupTabs([..._results]);
		setChosenOptions([]);
		setIsDisabled(initIcons);
		onData(Type.Tab, Type.Continue, props.groupId, [...groupTabs, ...results]);
		onUpdate(Type.Tab, true);
	};

	const onDelete = () => {
		const filteredTabs = groupTabs.filter((tab) => !tab.checked);
		/** @todo To check the empty tabs... */
		onData(Type.Tab, Type.Continue, props.groupId, [...filteredTabs]);
		setGroupTabs([...filteredTabs]);
		setIsCheckAll(false);
		onUpdate(Type.Tab, true);
	};

	const onCheckAll = (status) => {
		if (groupTabs.length === 0) return;
		setIsCheckAll(status);
		const results = handleAllChecked(groupTabs, status);
		setGroupTabs([...results]);
	};

	const onCheck = (id) => {
		const results = makeCheckedTabs(groupTabs, id);
		setGroupTabs([...results]);
	};

	const onHandle = (e, id) => {
		const results = handleRow(groupTabs, id, e.target.name, e.target.value);
		setGroupTabs([...results]);
		onData(Type.Tab, Type.Continue, props.groupId, [...results]);
		onUpdate(Type.Tab, true);
	};

	const onSetTabs = (values) => {
		setGroupTabs([...values]);
	};

	const onCreate = () => {
		const results = createSubtab(groupTabs);
		setGroupTabs([...results]);
		onData(Type.Tab, Type.Continue, props.groupId, [...results]);
		onUpdate(Type.Tab, true);
	};

	const onSave = async () => {
		try {
			setLoading(true);
			props.onMenu(Type.Tab, groupTabs, props.groupId);
			const res = await updateTabApi(groupTabs);
			onUpdate(Type.Tab, false);
			notifySuccess(res);
		} catch (error) {
			notifyError(error.message);
		} finally {
			setLoading(false);
		}
	};

	const onReset = () => {
		const results = assignOrder(Type.Tab, props.selectedTabs);
		setGroupTabs([...results]);
		setIsDisabled(initIcons);
		onData(Type.Tab, Type.Reset, props.groupId);
		onUpdate(Type.Tab, false);
	};

	/**
	 * @function checkOpenSubtab
	 * @param {object[]} groupTabs
	 * @returns {boolean}
	 */
	function checkOpenSubtab(groupTabs) {
		const isOpen = groupTabs.some((tab) => tab.open === true);
		return isOpen;
	}

	useEffect(() => {
		onReset();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.trgRst]);

	useEffect(() => {
		const result = checkOpenSubtab(groupTabs);
		setIsOpenSubtab(result);
	}, [groupTabs]);

	useEffect(() => {
		setIsDisabled((prev) => ({ ...prev, delete: count === 0 }));
	}, [count]);

	useEffect(() => {
		if (groupTabs.length === 0) {
			setFilteredTabs(props.tabs);
		} else if (groupTabs.length > 0) {
			const _count = countCheckedRow(groupTabs);
			const _tabs = props.tabs.filter((t) => !groupTabs.some((gt) => t.id === gt.id));
			setCount(_count);
			setFilteredTabs([..._tabs]);
		}
	}, [groupTabs, props.tabs]);

	const state = {
		groupTabs,
		filteredTabs,
		chosenOptions,
		isDisabled,
		isCheckAll,
		loading,
		isOpenSubtab,
	};

	const handleSubtab = {
		modal,
		onCloseModal,
		onSaveSubtab,
	};

	const handler = {
		onOptions,
		onAdd,
		onDelete,
		onCheckAll,
		onCheck,
		onToggle,
		onHandle,
		onSetTabs,
		onSave,
		onReset,
		onCreate,
	};

	return { state, handler, handleSubtab };
}

export default useTabs;
